'use client';
import { jsx } from 'react/jsx-runtime';
import classNames from 'classnames';
import { forwardRef, useRef, useState, useEffect } from 'react';
import { getTestLabels } from '../../utils/getTestLabels.js';
import style from './Chip.module.scss.js';

const { componentTestLabel } = getTestLabels('ChipGroup');
/**
 * [design (Figma)](* [design (Figma)](https://www.figma.com/design/MUnGlxUUspXSbRkFsEXWGQ/%F0%9F%92%A0-Core-Components?node-id=5618-27371&node-type=frame&t=78j1b1D2r7OjbLWy-0))
 * | [source code](https://src.private.zooplus.net/projects/FE/repos/ui-components/browse/src/components/Chip/ChipGroup.tsx)
 *
 * @param {string} [overflow] - Can be one of `scroll` or `wrap` (`wrap` is the implicit default).
 *   If set to `scroll`, the chips will be contained in a horizontally scrollable container on small screens rather than wrapping.
 *   Note that if the `ChipGroup` contains any `ActionChips`, then the chips will always wrap rather than scroll.
 */
const ChipGroup = forwardRef((props, ref) => {
    const { children, className, overflow, ...otherProps } = props;
    const containerRef = useRef(null);
    const [isFirstChipVisible, setIsFirstChipVisible] = useState(false);
    const [isLastVisible, setIsLastVisible] = useState(false);
    const firstChipCallback = (entries) => {
        const [entry] = entries;
        setIsFirstChipVisible(entry.isIntersecting);
    };
    const lastChipCallback = (entries) => {
        const [entry] = entries;
        setIsLastVisible(entry.isIntersecting);
    };
    useEffect(() => {
        const containerRefCurrent = containerRef.current;
        const options = {
            root: containerRefCurrent,
            threshold: 0.98,
        };
        if (!containerRefCurrent) {
            return;
        }
        const firstChip = containerRefCurrent.querySelector('.z-chip-wrap:first-of-type');
        const lastChip = containerRefCurrent.querySelector('.z-chip-wrap:last-of-type');
        const firstChipObserver = new IntersectionObserver(firstChipCallback, options);
        const lastChipObserver = new IntersectionObserver(lastChipCallback, options);
        firstChip && firstChipObserver.observe(firstChip);
        lastChip && lastChipObserver.observe(lastChip);
        return () => {
            if (containerRefCurrent) {
                firstChip && firstChipObserver.unobserve(firstChip);
                lastChip && lastChipObserver.unobserve(lastChip);
            }
        };
    }, [containerRef]);
    return (jsx("div", { ref: ref, "data-zta": componentTestLabel, className: classNames(className, style['z-chip-group-wrap']), ...otherProps, children: jsx("div", { ref: containerRef, className: classNames(className, style['z-chip-group'], {
                [style['z-chip-group--overflow-scroll']]: overflow === 'scroll',
                [style['z-chip-group--scroll-mask-start']]: !isFirstChipVisible,
                [style['z-chip-group--scroll-mask-end']]: !isLastVisible,
            }), children: children }) }));
});
ChipGroup.displayName = 'ChipGroup';

export { ChipGroup };
//# sourceMappingURL=ChipGroup.js.map
